import * as THREE from 'three';

export interface SerializedMesh {
	vertices: number[];
	indices: number[];
	matrix: number[];
}

/**
 * Serializes a THREE.Mesh for worker processing
 */
export const serializeMeshForWorker = (mesh: THREE.Mesh): SerializedMesh | null => {
	if (!mesh.geometry) return null;

	const geometry = mesh.geometry;
	const positionAttr = geometry.attributes.position;
	if (!positionAttr) return null;

	const vertices = Array.from(positionAttr.array);
	const indices = geometry.index ? Array.from(geometry.index.array) : [];
	const matrix = mesh.matrixWorld.toArray();

	return { vertices, indices, matrix };
};

/**
 * Gets all shadow-casting meshes from the scene, serialized for worker processing
 * Excludes windows for room interior sunlight analysis (to let light pass through)
 */
export const getShadowCastersForWorker = (scene: THREE.Scene): SerializedMesh[] => {
	const shadowCasters: SerializedMesh[] = [];

	scene.traverse((child) => {
		if (
			child instanceof THREE.Mesh &&
			child.castShadow &&
			!child.userData.isShadowIndicator &&
			!child.userData.isViewPointTarget &&
			!child.userData.isWindow
		) {
			const serialized = serializeMeshForWorker(child);
			if (serialized) {
				shadowCasters.push(serialized);
			}
		}
	});

	return shadowCasters;
};

/**
 * Gets shadow casters for facade analysis
 * Uses original building geometry (before CSG window holes) to ensure proper inter-facade shadowing
 */
export const getShadowCastersForFacade = (
	scene: THREE.Scene,
	buildingObject: THREE.Object3D,
	originalGeometriesMap: WeakMap<THREE.Mesh, THREE.BufferGeometry>
): SerializedMesh[] => {
	const shadowCasters: SerializedMesh[] = [];

	scene.traverse((child) => {
		if (
			child instanceof THREE.Mesh &&
			child.castShadow &&
			!child.userData.isShadowIndicator &&
			!child.userData.isViewPointTarget &&
			!child.userData.isWindow
		) {
			// For the building being analyzed, use original geometry (pre-CSG) if available
			if (child === buildingObject) {
				const originalGeometry = originalGeometriesMap.get(child);
				if (originalGeometry) {
					const vertices = Array.from(originalGeometry.getAttribute('position').array);
					const indices = originalGeometry.index ? Array.from(originalGeometry.index.array) : [];
					const matrix = child.matrixWorld.toArray();
					shadowCasters.push({ vertices, indices, matrix });
					return; // Skip fallback
				}
			}
			
			// For other objects or fallback, use current geometry
			const serialized = serializeMeshForWorker(child);
			if (serialized) {
				shadowCasters.push(serialized);
			}
		}
	});

	return shadowCasters;
};

/**
 * Serializes all visible meshes in the scene for worker processing
 * Excludes specific visualization groups
 */
export const serializeSceneMeshes = (
	scene: THREE.Scene,
	excludeGroups: string[] = ['facadeSunlightVisualization', 'wallPolygons']
): SerializedMesh[] => {
	const meshes: SerializedMesh[] = [];

	scene.traverse((object) => {
		if (object instanceof THREE.Mesh && object.visible) {
			// Skip excluded groups
			if (object.parent?.name && excludeGroups.includes(object.parent.name)) {
				return;
			}

			const geometry = object.geometry;
			if (geometry && geometry.attributes.position) {
				const positionAttr = geometry.attributes.position;
				const vertices = Array.from(positionAttr.array) as number[];
				const indices = geometry.index
					? (Array.from(geometry.index.array) as number[])
					: Array.from({ length: vertices.length / 3 }, (_, i) => i);

				const worldMatrix = object.matrixWorld;
				const matrixArray = worldMatrix.toArray();

				meshes.push({
					vertices,
					indices,
					matrix: matrixArray,
				});
			}
		}
	});

	return meshes;
};
